export const meta = {
  id: '04-phrases-1',
  order: 4,
  titleFi: 'Fraasit 1',
  titleEn: 'Phrases 1',
  duolingoUnit: 'duo-02-phrases-1',
  lemmaIds: ['kessa', 'issa', 'kepa', 'rytsas', 'hontes', 'urnegon', 'majaqagon', 'azantys', 'kirimvose', 'sylutegon', 'iotāptegon', 'ābra', 'lykso'],
  exampleIds: [
    'got-s3e07-003',
    'got-s4e05-004',
    'ex-ee655aa956a2',
    'ex-6955e6a2d324',
    'got-s5e03-011',
    'ex-e4d918a6dd2b',
    'ex-238a7b80dcce',
    'ex-ff8f69a8b78a',
    'ex-aca101ee6a97',
    'ex-0cbd31a691d2',
    'hotd-s2e03-001',
    'ex-df50ad0c9699',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Phrases_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Simple_coordination',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Infinitive',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Conjunctive_lengthening',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['stress'],
  showLines: ['got-s3e07-003', 'got-s4e05-004', 'got-s5e03-011', 'hotd-s2e03-001'],
  pronunciation: { focus: 'stress', words: ['urnegon', 'majaqagon', 'azantys', 'kirimvose', 'sylutegon'], lines: ['got-s3e07-003', 'got-s4e05-004', 'got-s5e03-011'] },
  phrases: ['kessa', 'issa', 'rytsas', 'kirimvose', 'lykso'],
  letter: {
    fi: 'Tervehdi, kiitä ja toivota hyvää – kirje kolmesta fraasista.',
    en: 'Greet, thank and wish well: a letter of three phrases.',
  },
  scene: 'got-s3e07-1',
  // the Halloween script (docs/COURSE.md "The Halloween script")
  halloween: ['ideas', 'esitys'],
  // Lukupino opens (engine pedagogy/reading.ts)
  reading: true,
}

# Fraasit 1

Tässä osiossa on vähän uutta kielioppia ja paljon hyödyllisiä sanoja: tervehdys, kiitos, kyllä. Lisäksi kerrataan <LessonLink n={1}>oppitunnin 1</LessonLink> painosääntö, joka on suomalaiselle koko kielen tärkein ääntämisasia.

## Tervehdys ja kiitos

<Pronounce l="rytsas" word="rytsas" />

<Hv glossed>Rytsas</Hv> on ’hei, terve, tervehdys’.

<Line id="got-s3e07-003" />

<Note>
Kun tervehdit useita ihmisiä kerralla, voit sanoa kuten Damien Halme (Academy of High Valyrian) videoidensa alussa (<Hv glossed>tolvys</Hv> ’jokainen, kaikki’). Tervehdys on harrastajan muodostama, ei sarjasta.

<Practice hv="Rytsas, tolvys!" fi="Hei kaikki!" />
</Note>

<Note>
**Meidän nimet.** Omat valyriankieliset nimenne valitsette yhdessä sivulla <NamesLink>Meidän nimet</NamesLink>: kumpikin äänestää. Kun nimi on päätetty, se tulee teidän kummankin päivän kortteihin. <LessonLink n={21}>Oppitunnilla 21</LessonLink> opitte puhuttelemaan toisianne nimeltä.
</Note>

<Pronounce l="kirimvose" word="kirimvose" />

<Hv glossed>Kirimvose</Hv> on ’kiitos’. Kiinnitä jo nyt huomiota painoon: *ki-RIM-vo-se*, ei *KI-rim-vo-se*. Siihen palataan alempana.

<Line id="got-s4e05-004" />

<Example id="ex-ee655aa956a2" />

’Kiitos ja ole hyvä’: tässä kaksi fraasia on yhdistetty ja-sanalla <Hv l="se">se</Hv>.

## Kyllä on ”on”

Sana, jolla vastataan ’kyllä’, on teille jo tuttu <LessonLink n={3}>edelliseltä oppitunnilta</LessonLink>: <Hv l="issa">issa</Hv>, sama sana kuin ’on’.

Suomalaiselle tämä on kotoisaa, kun kysytään olla-verbillä: *Onko se hyvä? – On.* Korkeavalyria vastaa samoin.

<Example id="ex-6955e6a2d324" />

Duolingon Tips & Notes -ohjeiden mukaan vastaus noudattaa yleensä kysymyksen aikamuotoa. Jos halutaan sanoa ’kyllä, siitä tulee hyvä’, sanotaan <Hv glossed>kessa</Hv> ’se tulee olemaan’ eikä <Hv glossed>issa</Hv> ’se on’. Tässä suomesta ei enää ole apua, koska suomessa ei ole taivutettua futuuria: vastaamme *on* myös tulevaisuutta koskevaan kysymykseen. Duolingossa käytetään toistaiseksi vain muotoa <Hv l="issa">issa</Hv>, koska futuuria ei ole vielä opeteltu.

<Pronounce l="kessa" word="kessa" />

<Line id="got-s5e03-011" />

<Example id="ex-e4d918a6dd2b" />

<Example id="ex-238a7b80dcce" />

Daenerysin lauseessa <Hv glossed>kessa</Hv> ei ole vastaus vaan tavallinen verbi: ’kaikki tulee olemaan hyvin’.

Olla-verbin kaksi kielteistä ilmausta, <Hv glossed>iksos daor</Hv> ’ei ole’ ja <Hv glossed>ikson daor</Hv> ’en ole’, opit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>. Muuten kieltäminen on korkeavalyriassa monimutkaisempaa kuin monessa muussa kielessä, ja se opetellaan myöhemmin.

## Paino: suomalaisen suurin ansa

Suomessa paino on aina sanan ensimmäisellä tavulla. Tapa on niin syvällä, ettei sitä huomaa – ja juuri siksi se tarttuu mukaan vieraaseen kieleen. Korkeavalyriassa painon paikkaa ei lasketa sanan alusta vaan sen **lopusta**.

Duolingon ohjeiden mukaan sääntö on tämä:

1. Paino on tavallisesti **toiseksi viimeisellä** tavulla.
2. Jos toiseksi viimeinen tavu on **kevyt** ja sitä edeltävä (kolmanneksi viimeinen) tavu on **raskas**, paino siirtyy kolmanneksi viimeiselle tavulle.

**Raskaassa tavussa** on pitkä vokaali tai laskeva diftongi (*ae*, *ao*), tai se päättyy konsonanttiin. Nouseva diftongi (*io*) on kevyt, jos sen jälkimmäinen vokaali on lyhyt (Peterson). **Kevyessä tavussa** on lyhyt vokaali, eikä sen perässä ole konsonanttia.

Käytännössä katsotaan siis aina ensin sanan toiseksi viimeistä tavua. Jos se on raskas, paino on siinä. Jos se on kevyt, katsotaan vielä yksi tavu taaksepäin.

### Kaksitavuiset: suomi on oikeassa

Kaksitavuisissa sanoissa paino osuu tavallisesti ensimmäiselle tavulle, joten suomalainen vaisto toimii. Poikkeukset (käskymuodot ja ja-sanan pidennys) ovat alempana.

<Pronounce word="kepa" />

<Pronounce l="rytsas" word="rytsas" />

<Pronounce l="issa" word="issa" />

<Pronounce l="vāedagon" word="vāedas" />

*KE-pa*, *RY-tsas*, *IS-sa*, *VĀE-das*: oli tavu kevyt tai raskas, kaksitavuisessa sanassa ensimmäinen tavu on aina se toiseksi viimeinen.

### Toiseksi viimeinen tavu on raskas

Tässä suomalainen erehtyy ensimmäisen kerran.

<Pronounce word="azandy" />

<Pronounce word="hontesse" />

<Pronounce word="azantyssy" />

*a-ZAN-dy* (’lyhyt miekka’), *hon-TES-se*, *a-zan-TYS-sy* (’ritarit’). Suomalainen sanoisi helposti *A-zan-dy*. Toiseksi viimeinen tavu on kuitenkin raskas (se päättyy konsonanttiin), joten paino on siinä.

### Toiseksi viimeinen on kevyt, sitä edeltävä raskas

<Pronounce word="urnesi" />

<Pronounce l="kirimvose" word="kirimvose" />

<Pronounce word="iotāptegon" />

*UR-ne-si*, *ki-RIM-vo-se*, *io-TĀP-te-gon*. Toiseksi viimeinen tavu (*ne*, *vo*, *te*) on kevyt ja sitä edeltävä raskas, joten paino hyppää tavun taaksepäin.

### Molemmat kevyitä

<Pronounce word="majaqis" />

<Pronounce word="majaqi" />

<Pronounce word="sylutegon" />

*ma-JA-qis*, *ma-JA-qi*, *sy-lu-TE-gon*. Kun sekä toiseksi että kolmanneksi viimeinen tavu ovat kevyitä, paino jää toiseksi viimeiselle. *Sylutegon* on hyvä harjoitussana: suomalaisena tekisi mieli sanoa *SY-lu-te-gon*.

<Note>
Suomessa pituus ja paino ovat eri asioita: sanassa *kaupungeissa* paino on ensimmäisellä tavulla, vaikka pitkiä tavuja on muuallakin. Korkeavalyriassa pituus ja paino kytkeytyvät toisiinsa – raskas tavu vetää painoa puoleensa. Kun kuulet pitkän vokaalin keskellä sanaa (*io-TĀP-te-gon*), paino on usein juuri siinä.
</Note>

### Kaksi poikkeusta

Duolingon ohjeet mainitsevat painosäännölle vain kaksi poikkeusta:

- **Käskymuodoissa** paino on aina viimeisellä tavulla, sekä yksikössä että monikossa.
- **Pidennys ja-sanan sijasta** siirtää painon luettelon viimeisen sanan loppuun, kuten <LessonLink n={2}>oppitunnilla 2</LessonLink>: *VA-la ā-BRĀ*.

<Line id="hotd-s2e03-001" />

Baela käskee lohikäärmettään: <Hv glossed>Sīmās!</Hv> ’Nouse!’ Käskymuodossa paino on viimeisellä tavulla.

## Uudet sanat lauseissa

Tässä osiossa on neljä uutta verbiä. Sanakirjassa verbi on infinitiivissä, ja tämän osion verbit päättyvät infinitiivissä kaikki *-gon*-päätteeseen: <Hv glossed>urnegon</Hv> ’nähdä’, <Hv glossed>majaqagon</Hv> ’ihailla’, <Hv glossed>sylutegon</Hv> ’maistaa’, <Hv glossed>iotāptegon</Hv> ’kunnioittaa’. Verbien päätteisiin tutustutaan kunnolla seuraavalla oppitunnilla.

<Pronounce word="majaqagon" />

<Example id="ex-ff8f69a8b78a" />

<Example id="ex-aca101ee6a97" />

<Hv>Majaqagon</Hv> tarkoittaa ihailemista etäältä tai niin, ettei ihailun kohde tiedä siitä – vähän kuin suomen *ihailla kaukaa*.

<Pronounce word="hontes" />

<Example id="ex-0cbd31a691d2" />

<Hv glossed>Hontesse</Hv> ’linnut’ on sanan <Hv>hontes</Hv> monikko – ja painoharjoituksista tuttu *hon-TES-se*.

Perhesanoista tähän osioon kuuluu <Hv glossed>kepa</Hv> ’isä’, ja <Hv glossed>ābra</Hv> on ’nainen’. <Hv glossed>Azantys</Hv> on ’ritari’.

<Pronounce word="azantys" />

## Halloween-käsikirjoitus

Halloweenina 2027 olette Targaryeneja, ja illan repliikit kootaan vähitellen käsikirjoitukseksi. Ensimmäinen repliikki on tämän oppitunnin tervehdys <Hv glossed>Rytsas</Hv> ’hei’. Jokaisesta käsikirjoituksen repliikistä tulee teille kummallekin kortti päivän harjoitukseen: kortissa on repliikki suomeksi, ja sinä sanot sen ääneen valyriaksi. Aluksi kortissa näkyy myös koko repliikki valyriaksi, sitten vain sanojen alkukirjaimet ja lopulta pelkkä suomi.

Kun keksit lauseen, jonka haluat osata sanoa juhlissa, kirjoita se suomeksi lauseideoihin. Lause käännetään valyriaksi tarkistussessiossa, ja kun hyväksytte käännöksen, lause siirtyy käsikirjoitukseen.

<HalloweenScript />

## Apufraasit

Kun sanat loppuvat kesken, näillä fraaseilla keskustelu jatkuu valyriaksi. Opettele ne kokonaisina. Yhteisen session Puhutaan-osiossa ne ovat käden ulottuvilla. Session voi tehdä myös yhdellä puhelimella: napauttakaa viikon Yhdessä-kortilla ”Kaksi puhelinta · vaihda”, niin sovellus kertoo, kumman vuoro on.

<Example id="ex-df50ad0c9699" />

<Hv glossed>Umbās</Hv> ’odota’ on käsky, joten paino on viimeisellä tavulla: *um-BĀS*.

<Pronounce l="lykso" word="lykso" />

<Hv glossed>Lykso</Hv> on ’ei hätää, kaikki hyvin’. Ja kun kumppanisi auttaa: <Hv glossed>Kirimvose</Hv> ’kiitos’.

## Harjoitellaan

<Practice hv="Kepa sȳz issa." fi="Isä on hyvä." />

<Practice hv="Issa, azantys sȳz issa." fi="Kyllä, ritari on hyvä." />

<Practice hv="Ābra sȳz issa." fi="Nainen on hyvä." />

Lue lauseet ääneen ja kuuntele painoa: *KE-pa*, *a-ZAN-tys*, *Ā-bra*.

## Lukupino

Tästä viikosta alkaen Lukupinossa on sinulle lauseita ja kohtauksia, joiden sanoista osaat jo vähintään 98 % korttien ansiosta. Sellaisen tekstin ymmärtää ilman sanakirjaa, ja tutut sanat vahvistuvat, kun ne tulevat vastaan uusissa lauseissa. Pitkässä tekstissä saa olla sana tai kaksi, joita et vielä osaa: ne näytetään merkityksineen ennen tekstiä. Lue jokainen teksti ääneen. Luettu teksti palaa pinoon noin viikon kuluttua ja uudelleen noin kuukauden kuluttua.

<Lukupino />

<Vocab />

<Check />
